<template>
  <div>
    <PageHeader title="Create Campaign" showBack />
    <b-card>
      <b-form @submit.prevent="handleCreate">
        <b-form-group label="Campaign Name"><b-form-input v-model="form.name" required /></b-form-group>
        <b-form-group label="Email Template">
          <b-form-select v-model="form.template" :options="templateOptions" required />
        </b-form-group>
        <b-form-group label="From Name"><b-form-input v-model="form.from_name" /></b-form-group>
        <b-form-group label="From Email"><b-form-input v-model="form.from_email" type="email" required /></b-form-group>
        <b-form-group label="Select Recipients by Tags">
          <v-select v-model="form.tag_ids" :options="tags" label="name" :reduce="t => t.id" multiple placeholder="Select tags..." />
          <div v-if="form.tag_ids && form.tag_ids.length" class="mt-2 small">
            <span v-if="isCountLoading" class="text-muted">
              <b-spinner small /> Counting recipients...
            </span>
            <span v-else>
              <a href="#" @click.prevent="openRecipientsModal" class="font-weight-bold">
                {{ recipientCount }} {{ recipientCount === 1 ? 'recipient' : 'recipients' }}
              </a>
              <span class="text-muted ml-1">match the selected tags</span>
            </span>
          </div>
        </b-form-group>
        <div class="d-flex">
          <b-button type="submit" variant="primary" :disabled="isSaving" class="mr-2">{{ isSaving ? 'Creating...' : 'Create Campaign' }}</b-button>
          <b-button variant="outline-primary" :disabled="isSaving" @click="showScheduleModal = true">Schedule</b-button>
        </div>
      </b-form>
    </b-card>

    <!-- Schedule Modal -->
    <b-modal v-model="showScheduleModal" title="Schedule Campaign" @ok="handleScheduleCreate" ok-title="Create & Schedule" :ok-disabled="!scheduleDate || !scheduleTime || isSaving" centered>
      <p class="text-muted mb-3">Choose when this campaign should be sent:</p>
      <b-form-group label="Date">
        <b-form-datepicker v-model="scheduleDate" :min="minDate" />
      </b-form-group>
      <b-form-group label="Time">
        <b-form-timepicker v-model="scheduleTime" />
      </b-form-group>
    </b-modal>

    <!-- Recipients Preview Modal -->
    <b-modal v-model="showRecipientsModal" title="Recipients Preview" size="lg" hide-footer centered>
      <p class="text-muted mb-3">{{ recipientCount }} {{ recipientCount === 1 ? 'recipient matches' : 'recipients match' }} the selected tags.</p>
      <b-table :items="recipients" :fields="recipientFields" :busy="isRecipientsLoading" show-empty class="table-nowrap mb-0" responsive>
        <template #cell(full_name)="data">
          {{ data.item.first_name }} {{ data.item.last_name }}
        </template>
        <template #cell(tags)="data">
          <b-badge v-for="tag in data.item.tags" :key="tag.id" :style="{ backgroundColor: tag.color }" class="mr-1 text-white">
            {{ tag.name }}
          </b-badge>
        </template>
        <template #table-busy>
          <div class="text-center my-3"><b-spinner /></div>
        </template>
      </b-table>
      <div v-if="recipientCount > recipientsPerPage" class="mt-2 d-flex justify-content-end">
        <Pagination :totalRows="recipientCount" :perPage="recipientsPerPage" v-model="recipientsPage" @change="fetchRecipientsPage" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '../../components/Ui/PageHeader.vue';
import Pagination from '../../components/Ui/Pagination.vue';

export default {
  name: 'CreateCampaign',
  components: { PageHeader, Pagination },
  data() {
    return {
      form: { name: '', template: null, from_name: '', from_email: '', tag_ids: [], contact_ids: [] },
      templates: [],
      tags: [],
      isSaving: false,
      showScheduleModal: false,
      scheduleDate: '',
      scheduleTime: '',
      // recipient preview
      recipientCount: 0,
      isCountLoading: false,
      countDebounce: null,
      showRecipientsModal: false,
      recipients: [],
      recipientsPage: 1,
      recipientsPerPage: 15,
      isRecipientsLoading: false,
      recipientFields: [
        { key: 'full_name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'tags', label: 'Tags' },
      ],
    };
  },
  computed: {
    templateOptions() {
      return [{ value: null, text: 'Select template...' }, ...this.templates.map(t => ({ value: t.id, text: t.name }))];
    },
    minDate() {
      return new Date().toISOString().split('T')[0];
    },
  },
  watch: {
    'form.tag_ids': {
      handler() {
        if (this.countDebounce) clearTimeout(this.countDebounce);
        this.countDebounce = setTimeout(() => {
          this.fetchRecipientCount();
        }, 250);
      },
    },
  },
  methods: {
    ...mapActions('campaign', ['createCampaign', 'scheduleCampaign']),
    ...mapActions('emailTemplate', ['getAllEmailTemplates']),
    ...mapActions('tag', ['getAllTags']),
    ...mapActions('contact', ['getAllContacts']),
    async fetchRecipientCount() {
      if (!this.form.tag_ids || !this.form.tag_ids.length) {
        this.recipientCount = 0;
        return;
      }
      this.isCountLoading = true;
      try {
        const res = await this.getAllContacts({
          tag_ids: this.form.tag_ids.join(','),
          limit: 1,
          offset: 0,
        });
        this.recipientCount = res?.data?.count ?? res?.count ?? 0;
      } catch (err) {
        this.recipientCount = 0;
      } finally {
        this.isCountLoading = false;
      }
    },
    async openRecipientsModal() {
      this.recipientsPage = 1;
      this.showRecipientsModal = true;
      await this.fetchRecipientsPage(1);
    },
    async fetchRecipientsPage(page) {
      this.recipientsPage = page;
      this.isRecipientsLoading = true;
      try {
        const res = await this.getAllContacts({
          tag_ids: this.form.tag_ids.join(','),
          limit: this.recipientsPerPage,
          offset: (page - 1) * this.recipientsPerPage,
        });
        this.recipients = res?.data?.results || res?.results || [];
        this.recipientCount = res?.data?.count ?? res?.count ?? 0;
      } catch (err) {
        this.recipients = [];
      } finally {
        this.isRecipientsLoading = false;
      }
    },
    async handleCreate() {
      this.isSaving = true;
      try {
        const user = this.$store.getters['auth/getLoggedInUser'];
        await this.createCampaign({ ...this.form, school: user.school.id });
        this.makeToast('success', 'Campaign created');
        this.$router.push({ name: 'campaigns-list' });
      } catch (err) { this.makeToast('danger', 'Failed to create campaign'); }
      finally { this.isSaving = false; }
    },
    async handleScheduleCreate(evt) {
      evt.preventDefault();
      if (!this.scheduleDate || !this.scheduleTime) return;
      this.isSaving = true;
      try {
        const user = this.$store.getters['auth/getLoggedInUser'];
        const res = await this.createCampaign({ ...this.form, school: user.school.id });
        const campaignId = res.data.data ? res.data.data.id : res.data.id;
        const scheduled_at = `${this.scheduleDate}T${this.scheduleTime}`;
        await this.scheduleCampaign({ id: campaignId, data: { scheduled_at } });
        this.makeToast('success', 'Campaign scheduled');
        this.showScheduleModal = false;
        this.$router.push({ name: 'campaigns-list' });
      } catch (err) { this.makeToast('danger', 'Failed to schedule campaign'); }
      finally { this.isSaving = false; }
    },
  },
  async created() {
    try {
      const user = this.$store.getters['auth/getLoggedInUser'];
      const school = user && user.school ? user.school : null;
      if (school && school.resend_from_email && !this.form.from_email) {
        this.form.from_email = school.resend_from_email;
      }
      if (school && school.name && !this.form.from_name) {
        this.form.from_name = school.name;
      }
      const [tplRes, tagRes] = await Promise.all([this.getAllEmailTemplates(), this.getAllTags()]);
      this.templates = tplRes.data.results || tplRes.data || [];
      this.tags = tagRes.data.results || tagRes.data || [];
    } catch (err) { /* ignore */ }
  },
};
</script>
